import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SalesOrderResultDTO } from '@app/api/purchase-service-sales';
import { PermissionService } from '@app/core/services/permission.service';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import {
  salesOrderSeverity,
  salesOutboundSeverity,
} from '../../distribution.constants';
import { SalesOrderService } from '../services/sales-order.service';

@Component({
  selector: 'app-sales-order-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    TableModule,
    TagModule,
    ToastModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './sales-order-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesOrderViewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(SalesOrderService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  readonly statusSeverity = salesOrderSeverity;
  readonly outboundStatusSeverity = salesOutboundSeverity;
  readonly order = signal<SalesOrderResultDTO | null>(null);
  readonly loading = signal(false);

  private readonly status = computed(() => this.order()?.status);
  readonly canApproveAction = computed(() => this.can('approve'));
  readonly canReleaseAction = computed(() => this.can('release'));
  readonly canRejectAction = computed(() => this.can('reject'));
  readonly canCancelAction = computed(() => this.can('cancel'));

  readonly showApprove = computed(
    () => this.status() === 'PENDING' && this.canApproveAction(),
  );
  readonly showReject = computed(
    () => this.status() === 'PENDING' && this.canRejectAction(),
  );
  readonly showRelease = computed(
    () => this.status() === 'APPROVED' && this.canReleaseAction(),
  );
  readonly showCancel = computed(
    () =>
      (this.status() === 'PENDING' || this.status() === 'APPROVED') &&
      this.canCancelAction(),
  );

  private can(action: string): boolean {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `distribution.sales_order.${action}`,
    );
  }

  ngOnInit(): void {
    const id = parsePositiveIntRouteParam(this.route.snapshot.paramMap.get('id'));
    if (id == null) {
      this.onBack();
      return;
    }
    this.load(id);
  }

  onBack(): void {
    this.router.navigate(['/distribution/sales-order']);
  }

  onApprove(): void {
    this.confirm('distribution.sales_order.confirm_approve', () =>
      this.service.approve(this.order()!.id!),
    );
  }

  onRelease(): void {
    this.confirm('distribution.sales_order.confirm_release', () =>
      this.service.release(this.order()!.id!),
    );
  }

  onReject(): void {
    this.confirm('distribution.sales_order.confirm_reject', () =>
      this.service.reject(this.order()!.id!),
    );
  }

  onCancel(): void {
    this.confirm('distribution.sales_order.confirm_cancel', () =>
      this.service.cancel(this.order()!.id!),
    );
  }

  private confirm(
    messageKey: string,
    action: () => ReturnType<SalesOrderService['approve']>,
  ): void {
    this.confirmationService.confirm({
      message: this.translate.instant(messageKey, {
        series: this.order()?.series,
      }),
      accept: () =>
        action().subscribe({
          next: (updated) => {
            this.order.set(updated);
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.messages.success'),
              detail: this.translate.instant('distribution.messages.updated'),
            });
          },
          error: (err) => this.showError(err),
        }),
    });
  }

  private load(id: number): void {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (data) => {
        this.order.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.showError(err);
      },
    });
  }

  private showError(err: unknown): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail:
        (err as { error?: { message?: string } })?.error?.message ??
        this.translate.instant('distribution.messages.failed'),
    });
  }
}
